import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signupCompany } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, Mail, Lock, User, Phone, Loader2, ArrowLeft } from 'lucide-react';

const SignupPage = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    company_name: '',
    admin_name: '',
    admin_email: '',
    password: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await signupCompany(form);
      await checkAuth();
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Nine Degree</h1>
              <p className="text-xs text-muted-foreground">Inventory System</p>
            </div>
          </div>

          <Card className="border-border">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold">Create your account</CardTitle>
              <CardDescription>
                Set up your company and get started with inventory management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="company_name"
                      type="text"
                      placeholder="e.g. Prestige Group"
                      className="pl-10"
                      value={form.company_name}
                      onChange={(e) => updateField('company_name', e.target.value)}
                      required
                      data-testid="signup-company-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin_name">Admin Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="admin_name"
                      type="text"
                      placeholder="Your full name"
                      className="pl-10"
                      value={form.admin_name}
                      onChange={(e) => updateField('admin_name', e.target.value)}
                      required
                      data-testid="signup-admin-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin_email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="admin_email"
                      type="email"
                      placeholder="admin@company.com"
                      className="pl-10"
                      value={form.admin_email}
                      onChange={(e) => updateField('admin_email', e.target.value)}
                      required
                      data-testid="signup-admin-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Min 6 characters"
                      className="pl-10"
                      value={form.password}
                      onChange={(e) => updateField('password', e.target.value)}
                      required
                      minLength={6}
                      data-testid="signup-password"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number (Optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+91 9876543210"
                      className="pl-10"
                      value={form.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      data-testid="signup-phone"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="signup-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create Company Account'
                  )}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                  data-testid="signup-login-link"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Already have an account? Sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 login-bg relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary/70" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <h2 className="text-4xl font-bold mb-4">
            Start Managing Your Inventory
          </h2>
          <p className="text-lg text-white/80 max-w-md">
            Create your company account in seconds. Get full control over unit availability,
            sales channels, and booking workflows.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
